import { useEffect, useState } from "react";
import {
  usePunditUserContext,
  useSpaceGroupsContext,
  useSpacesContext,
} from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { isSpaceVisibleOnSidebar } from "@circle-react/helpers/spaceHelpers";
import type { Space, SpaceGroup } from "@circle-react/types";

interface UseSpaceGroupsListProps {
  valueKey: keyof SpaceGroup;
}

export const useSpaceGroupsList = ({ valueKey }: UseSpaceGroupsListProps) => {
  const [visibleSpaceGroups, setVisibleSpaceGroups] = useState<SpaceGroup[]>(
    [],
  );
  const { currentCommunityMember, isLoading: isCommunityLoading } =
    usePunditUserContext();

  const {
    helpers: { isDataLoading: isSpaceGroupsDataLoading },
    records: spaceGroups,
  } = useSpaceGroupsContext();
  const {
    helpers: { isDataLoading: isSpacesDataLoading, spaceVisibleOnSidebar },
  } = useSpacesContext();
  const spaces = spaceVisibleOnSidebar();
  const isLoading =
    isSpacesDataLoading() || isSpaceGroupsDataLoading() || isCommunityLoading;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setVisibleSpaceGroups(
      spaceGroups.filter((spaceGroup: SpaceGroup) => {
        if (isCommunityAdmin(currentCommunityMember)) {
          return true;
        }

        return spaces.some(
          (space: Space) =>
            space.space_group_id === spaceGroup.id &&
            isSpaceVisibleOnSidebar({ space, spaceGroup }),
        );
      }),
    );
  }, [isLoading, spaces, spaceGroups, currentCommunityMember]);

  if (isLoading && !visibleSpaceGroups.length) {
    return {
      visibleSpaceGroups: [],
      isLoading,
    };
  }

  return {
    visibleSpaceGroups: visibleSpaceGroups.map(record => ({
      label: record.name,
      value: record[valueKey] as string,
    })),
    isLoading,
  };
};
